/**
 * Vínculo indicador ↔ instrumento.
 *
 * Es la única forma de saber qué instrumentos miden un indicador: no existe una
 * columna `instrumentos.id_indicador`.
 *
 * Store Pinia: estado compartido del módulo y orquestación de sus servicios.
 * Las vistas no llaman a `http` ni a los servicios directamente; pasan por aquí.
 * Los servicios no lanzan, así que las acciones interpretan valores neutros
 * (`[]`, `null`, `false`) como fallo.
 */

import { defineStore } from 'pinia'
import type IndicadorInstrumento from '@/interfaces/IndicadorInstrumento'
import {getIndicadorInstrumentosService, getIndicadorInstrumentoByIdService, crearIndicadorInstrumentoService, eliminarIndicadorInstrumentoService} from '@/services/IndicadorInstrumentoService'

/** Store `GestionIndicadorInstrumentos`. Ver la cabecera del archivo para el contrato del módulo. */
export const useGestionIndicadorInstrumentos = defineStore('GestionIndicadorInstrumentos', {
  state: () => ({
    relaciones: [] as IndicadorInstrumento[],
    relacionActual: null as IndicadorInstrumento | null,
    urlRelaciones: `${process.env.VUE_APP_API_BASE_URL}/api/indicador-instrumentos`
  }),

  actions: {
    async getRelaciones() {
      try {
        const data = await getIndicadorInstrumentosService(this.urlRelaciones)
        this.relaciones = data
        return data
      } catch (err) {
        console.error('Error al obtener relaciones:', err)
        return []
      }
    },

    async getRelacionById(id: number) {
      try {
        const data = await getIndicadorInstrumentoByIdService(this.urlRelaciones, id)
        if (data) this.relacionActual = data
        return data
      } catch (err) {
        console.error(`Error al obtener relación con id ${id}:`, err)
        throw err
      }
    },

    async crearRelacion(payload: Omit<IndicadorInstrumento, 'id'>) {
      try {
        const nueva = await crearIndicadorInstrumentoService(this.urlRelaciones, payload)
        if (nueva) {
          const existe = this.relaciones.find(r => r.id === nueva.id)
          if (!existe) {
            this.relaciones.push(nueva)
          }
          this.relacionActual = nueva
        }
        return nueva
      } catch (err) {
        console.error('Error al crear relación:', err)
        throw err
      }
    },

    async eliminarRelacion(id: number) {
      try {
        const ok = await eliminarIndicadorInstrumentoService(this.urlRelaciones, id)
        if (ok) {
          this.relaciones = this.relaciones.filter(r => r.id !== id)
        }
        return ok
      } catch (err) {
        console.error(`Error al eliminar relación con id ${id}:`, err)
        throw err
      }
    }
  }
})